import { unwrap, type SetupDraftOut, type WorkspaceOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Select, Textarea, toast } from "@sagot/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { applyBody, configFromDraft } from "../../../../onboarding/setup-config";
import { draftRequest } from "../../../../onboarding/use-setup-draft";
import { useApi } from "../../../../session/session-provider";

const NEW_AGENT = "new";

function DraftSummary({ draft }: { draft: SetupDraftOut }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 rounded-lg border border-line bg-surface-2 p-3 text-sm" data-testid="resuggest-summary">
      <div className="font-bold">{draft.assistant_name}</div>
      <p className="text-xs text-fg-muted">{t("settings.setup.summaryStarters", { starters: draft.starter_questions.join(" · ") })}</p>
      <p className="text-xs text-fg-muted">{t("settings.setup.summaryStages", { stages: draft.lead_stages.map((s) => s.name).join(" → ") })}</p>
    </div>
  );
}

function DiffTable({ draft, agentName }: { draft: SetupDraftOut; agentName: string }) {
  const { t } = useTranslation();
  const rows = draft.diff ?? [];
  return (
    <div className="space-y-2" data-testid="resuggest-diff">
      <h3 className="text-sm font-bold">{t("settings.setup.diffTitle", { name: agentName })}</h3>
      {rows.length === 0 ? <p className="text-sm text-fg-muted">{t("settings.setup.noDiff")}</p> : null}
      {rows.map((row) => (
        <div key={row.field} className="grid grid-cols-1 gap-2 rounded-lg border border-line p-3 text-xs sm:grid-cols-2">
          <div className="font-semibold sm:col-span-2">{t(`settings.setup.fields.${row.field}`, { defaultValue: row.field })}</div>
          <div>
            <div className="text-fg-subtle">{t("settings.setup.current")}</div>
            <p className="whitespace-pre-wrap text-fg-muted">{row.current || "—"}</p>
          </div>
          <div>
            <div className="text-fg-subtle">{t("settings.setup.suggested")}</div>
            <p className="whitespace-pre-wrap text-fg">{row.suggested || "—"}</p>
          </div>
        </div>
      ))}
      <p className="text-xs text-fg-subtle">{t("settings.setup.neverOverwrite")}</p>
    </div>
  );
}

interface ResuggestDialogProps {
  workspace: WorkspaceOut;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Settings → Organization: a fresh draft for a NEW agent, or a read-only diff for an existing one. */
export function ResuggestDialog({ workspace, open, onOpenChange }: ResuggestDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [target, setTarget] = useState(NEW_AGENT);
  const [description, setDescription] = useState("");
  const agents = useQuery({ queryKey: ["ws", workspace.id, "agents", "resuggest"], queryFn: () => unwrap(api.GET("/v1/agents")), enabled: open });
  const suggest = useMutation({
    mutationFn: () => {
      const body = { ...draftRequest(workspace, description.trim() ? "ai" : "template", description), template_key: undefined };
      return unwrap(api.POST("/v1/workspace/setup-draft", { body: target === NEW_AGENT ? body : { ...body, agent_id: target } }));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const create = useMutation({
    mutationFn: (draft: SetupDraftOut) => unwrap(api.POST("/v1/workspace/setup/apply", { body: { ...applyBody(configFromDraft(draft)), apply_workspace: false } })),
    onSuccess: (created) => {
      toast.success(t("settings.setup.created", { name: created.agent_name }));
      onOpenChange(false);
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const items = agents.data?.items ?? [];
  const agentName = items.find((a) => a.id === target)?.name ?? "";
  const draft = suggest.data;
  const footer = draft ? (
    <Button loading={create.isPending} onClick={() => { create.mutate(draft); }}>
      {t("settings.setup.createNew")}
    </Button>
  ) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t("settings.setup.resuggestTitle")} description={t("settings.setup.resuggestBody")} footer={footer}>
        <div className="space-y-4">
          <Field label={t("settings.setup.target")}>
            <Select value={target} onChange={(e) => { setTarget(e.target.value); suggest.reset(); }}>
              <option value={NEW_AGENT}>{t("settings.setup.targetNew")}</option>
              {items.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("settings.setup.describe")}>
            <Textarea rows={3} maxLength={1_000} value={description} onChange={(e) => { setDescription(e.target.value); }} />
          </Field>
          <Button variant="secondary" loading={suggest.isPending} onClick={() => { suggest.mutate(); }}>
            {t("settings.setup.suggest")}
          </Button>
          {draft ? <DraftSummary draft={draft} /> : null}
          {draft && target !== NEW_AGENT ? <DiffTable draft={draft} agentName={agentName} /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
